<template>
  <v-card height="100%" flat color="bg1" class="register-management">
    <!-- Main Content Wrapper (flex container for content + sidebar) -->
    <div class="register-management-layout">
      <!-- Main Content -->
      <div class="register-management-content">
    <!-- Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'" style="max-height: 65px;">
      <v-list-item-content>
        <v-list-item-title class="title">Register Management</v-list-item-title>
        <v-list-item-subtitle class="subtitle-1">
          Create and manage registers for Facilities and Facility Groups
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action class="d-flex flex-row align-center" style="gap: 12px;">
        <v-btn color="primary" @click="openCreateDialog" v-if="$has(permissions.Registers.Create)">
          <v-icon left>mdi-plus</v-icon>
          Create Register
        </v-btn>
      </v-list-item-action>
    </v-list-item>

    <v-divider></v-divider>

    <!-- Content -->
    <v-sheet color="transparent" class="rm-body">
      <!-- Toolbar: owner type, owner, search, Mode filter, count (replaces the Facility Selection card) -->
      <div class="rm-toolbar">
        <v-btn-toggle
          v-model="ownerType"
          mandatory
          dense
          color="primary"
          class="rm-owner-type"
          aria-label="Owner type">
          <v-btn
            v-for="option in ownerTypeOptions"
            :key="option.value"
            :value="option.value"
            :aria-pressed="String(ownerType === option.value)"
            text>
            {{ option.text }}
          </v-btn>
        </v-btn-toggle>

        <v-autocomplete
          attach
          v-model="selectedOwnerId"
          :items="ownerItems"
          :disabled="isAllOwners"
          :placeholder="ownerPlaceholder"
          :prepend-inner-icon="ownerSelectIcon"
          :aria-label="ownerPlaceholder"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details
          class="rm-owner-select"
          @change="onOwnerChange" />

        <v-text-field
          v-model="search"
          placeholder="Search registers"
          aria-label="Search registers"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="rm-search" />

        <v-select
          v-if="showModeFilter"
          attach
          v-model="modeFilter"
          :items="modeFilterOptions"
          label="Mode"
          aria-label="Filter registers by Facility assignment mode"
          outlined
          dense
          hide-details
          class="rm-mode-filter" />

        <v-spacer></v-spacer>
        <span class="rm-count text--secondary" aria-live="polite">{{ countLabel }}</span>
      </div>

      <!-- Register grid -->
      <div class="rm-table-shell">
        <v-data-table
          :headers="registerHeaders"
          :items="tableRows"
          :loading="loading"
          :search="search"
          item-key="id"
          fixed-header
          class="rm-table"
          :items-per-page="15"
          :footer-props="{
            itemsPerPageOptions: [15, 30, 50, 100],
          }"
          @pagination="onPagination">
          <template v-slot:[`header.actions`]>
            <span class="d-sr-only">Actions</span>
          </template>

          <!-- Name Column (sticky identity column) -->
          <template v-slot:[`item.name`]="{ item }">
            <div class="rm-name">
              <template v-if="item.hasTagLinks">
                <v-icon small color="primary" class="mr-1">mdi-link-variant</v-icon>
                <span class="d-sr-only">Has tag links.</span>
              </template>
              <span class="rm-name__text" :title="item.name">{{ item.name }}</span>
            </div>
          </template>

          <!-- Owner Column: two-line block (icon + name / kind · mode) -->
          <template v-slot:[`item.ownerName`]="{ item }">
            <div v-if="item.ownerView.kindLabel" class="rm-owner">
              <v-icon size="18" class="rm-owner__icon" aria-hidden="true">{{ item.ownerView.icon }}</v-icon>
              <div class="rm-owner__text">
                <span class="rm-owner__name" :title="item.ownerView.name">{{ item.ownerView.name }}</span>
                <span class="rm-owner__meta text--secondary">
                  <span>{{ item.ownerView.kindLabel }}</span>
                  <template v-if="item.ownerView.mode">
                    <span class="rm-owner__sep text--disabled" aria-hidden="true">·</span>
                    <span
                      class="rm-owner__mode"
                      :class="`rm-owner__mode--${item.ownerView.mode.tone}`"
                      :title="item.ownerView.mode.description">
                      <v-icon size="14" aria-hidden="true">{{ item.ownerView.mode.icon }}</v-icon>
                      {{ item.ownerView.mode.label }}
                      <span class="d-sr-only">: {{ item.ownerView.mode.description }}</span>
                    </span>
                  </template>
                </span>
              </div>
            </div>
            <span v-else class="text--disabled">—</span>
          </template>

          <!-- Type Column: dot + text -->
          <template v-slot:[`item.type`]="{ item }">
            <span v-if="item.type" class="rm-dot-text">
              <span class="rm-dot" :class="typeDotClass(item.type)" aria-hidden="true"></span>
              {{ getTypeText(item.type) }}
            </span>
            <span v-else class="rm-dot-text text--secondary">
              <v-icon size="16" aria-hidden="true">mdi-tag-off-outline</v-icon>
              Non Tag
            </span>
          </template>

          <!-- Status Column: dot + text (hollow dot when inactive / unknown) -->
          <template v-slot:[`item.status`]="{ item }">
            <span class="rm-dot-text">
              <span class="rm-dot" :class="statusDotClass(item.status)" aria-hidden="true"></span>
              {{ getStatusText(item.status) }}
            </span>
          </template>

          <!-- Assigned Users Column (count) -->
          <template v-slot:[`item.assignedUsers`]="{ item }">
            <AssignedUsersCell :users="item.assignedUsers || []" />
          </template>

          <!-- Created Column (boxed date, kept from the previous design) -->
          <template v-slot:[`item.createDate`]="{ item }">
            <div v-if="item.createDate || item.createdAt" class="date-cell">
              <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
              {{ $formatDate(item.createDate || item.createdAt) }}
            </div>
            <span v-else class="text--disabled">—</span>
          </template>

          <!-- Updated Column -->
          <template v-slot:[`item.updateDate`]="{ item }">
            <div v-if="item.updateDate || item.updatedAt" class="date-cell">
              <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
              {{ $formatDate(item.updateDate || item.updatedAt) }}
            </div>
            <span v-else class="text--disabled">—</span>
          </template>

          <!-- Actions Column: 3 primary icons on hover / focus, everything else in the ⋮ menu -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="rm-actions">
              <span class="rm-actions__primary">
                <v-tooltip v-for="action in rowActions.primary" :key="action.key" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      :aria-label="`${action.label}: ${item.name}`"
                      v-bind="attrs"
                      v-on="on"
                      @click="runRowAction(action, item)">
                      <v-icon size="18">{{ action.icon }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ action.label }}</span>
                </v-tooltip>
              </span>

              <v-menu v-if="rowActions.menu.length || rowActions.danger.length" bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    title="More actions"
                    :aria-label="`More actions: ${item.name}`"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="rm-menu" :aria-label="`More actions for ${item.name}`">
                  <v-list-item v-for="action in rowActions.menu" :key="action.key" @click="runRowAction(action, item)">
                    <v-list-item-icon class="rm-menu__icon">
                      <v-icon size="18">{{ action.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ action.label }}</v-list-item-title>
                  </v-list-item>
                  <v-divider v-if="rowActions.menu.length && rowActions.danger.length" class="my-1"></v-divider>
                  <v-list-item v-for="action in rowActions.danger" :key="action.key" @click="runRowAction(action, item)">
                    <v-list-item-icon class="rm-menu__icon">
                      <v-icon size="18" :class="dangerTextClass">{{ action.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title :class="dangerTextClass">{{ action.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center d-flex flex-column">
              <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
              <p class="font-weight-medium">No Data Available!</p>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-sheet>

    <!-- Dialogs -->
    <create-register
      v-model="showCreateRegister"
      :register="selectedRegister"
      @save="onCreateRegisterSave" />

    <excel-upload
      v-model="showExcelUpload"
      :register="selectedRegister"
      :initial-file="importFile"
      @complete="onExcelUploadComplete" />

    <excel-upload
      v-model="showSchemaUpdate"
      :register="selectedRegister"
      mode="schema-update"
      @complete="onSchemaUpdateComplete" />

    <!-- Export Dialog -->
    <export-dialog
      v-model="showExportDialog"
      :register="registerToExport"
      :selected-row-ids="[]"
      :current-filters="{}"
      :table-columns="[]"
      :export-mode="ExportMode.Filtered"
      :total-rows="0"
      @complete="onExportComplete" />

    <!-- API Data Source Dialogs -->
    <api-data-source-list
      v-model="showApiDataSources"
      :register-id="selectedRegister && selectedRegister.id"
      @create="openApiWizard"
      @edit="editApiDataSource"
      @view-history="openApiHistory" />

    <api-data-source-wizard
      v-model="showApiWizard"
      :register="selectedRegister"
      :edit-data-source="editingApiDataSource"
      @complete="onApiDataSourceComplete" />

    <api-execution-history
      v-model="showApiHistory"
      :data-source="historyDataSource"
      :register="selectedRegister"
      @edit="editApiDataSourceFromHistory" />

    <!-- Register Data Viewer Dialog -->
    <v-dialog
      v-model="showRegisterDataViewer"
      max-width="95vw"
      max-height="90vh"
      persistent
      scrollable>
      <v-card height="90vh" class="d-flex flex-column">
        <v-card-title class="py-3 px-4 d-flex align-center">
          <v-icon color="success" class="mr-2">mdi-table-eye</v-icon>
          <span class="text-h6">{{ (selectedRegister && selectedRegister.name) || 'Register Data' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeRegisterDataViewer">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0 flex-grow-1" style="overflow: hidden;">
          <register-admin-workspace
            v-if="showRegisterDataViewer && selectedRegister"
            :register="selectedRegister"
            :current-register-type="getRegisterTypeString(selectedRegister.type)"
            :initial-tab="registerWorkspaceInitialTab"
            :key="`register-workspace-${selectedRegister.id}`"
            @showIn3d="handleShowIn3d" />
        </v-card-text>
      </v-card>
    </v-dialog>

      </div>
      <!-- End Main Content -->

      <!-- Audit History Sidebar -->
      <RegisterAuditSidebar
        :register-id="auditRegisterId"
        :register-name="auditRegisterName"
        hide-mode-toggle />
    </div>
    <!-- End Layout Wrapper -->
  </v-card>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { $has } from '@/utils';
  import permissions from '@Share/config/permissions';
  import {
    RegisterType,
    getRegisterTypeText,
    getRegisterStatusText,
  } from '../../config/enums.js';
  import {
    OWNER_TYPE,
    MODE_FILTER,
    MODE_FILTER_OPTIONS,
    registerOwnerPresentation,
    matchesOwnerFilter,
    matchesModeFilter,
    registerFetchParams,
    ownerSelectPlaceholder,
    registerCountLabel,
    registerTypeColorClass,
    registerStatusDotClass,
    resolveRegisterRowActions,
    clearRegisterDataErrorMessage,
  } from '../../modules/registerManagementPresentation.js';
  import CreateRegister from './CreateRegister.vue';
  import ExcelUpload from './ExcelUpload.vue';
  import ExportDialog from './ExportDialog.vue';
  import AssignedUsersCell from './AssignedUsersCell.vue';
  import RegisterAdminWorkspace from './RegisterAdminWorkspace.vue';
  import { RegisterAuditSidebar } from '../audit';
  import ApiDataSourceWizard from './ApiDataSourceWizard.vue';
  import ApiDataSourceList from './ApiDataSourceList.vue';
  import ApiExecutionHistory from './ApiExecutionHistory.vue';
  import { ExportMode } from '../../config/enums';
  import { $formatDate } from '@Utils';
  import { warn, error } from '@/utils/debugLogger';
  import facilityService from '@Facility/service/facility.service';
  import { REGISTER_IMPORT_ROUTE, takeImportFile, declineImportFile } from '@/actions/registerImport';

  export default {
    name: 'RegisterManagement',
    components: {
      CreateRegister,
      ExcelUpload,
      ExportDialog,
      AssignedUsersCell,
      RegisterAdminWorkspace,
      RegisterAuditSidebar,
      ApiDataSourceWizard,
      ApiDataSourceList,
      ApiExecutionHistory,
    },
    data() {
      return {
        permissions,
        // Toolbar filters (component state only; the notification deep link clears the query).
        ownerType: OWNER_TYPE.ALL,
        selectedOwnerId: null,
        modeFilter: MODE_FILTER.ALL,
        ownerTypeOptions: [
          { text: 'All', value: OWNER_TYPE.ALL },
          { text: 'Facility', value: OWNER_TYPE.FACILITY },
          { text: 'Facility Group', value: OWNER_TYPE.GROUP },
        ],
        modeFilterOptions: MODE_FILTER_OPTIONS,
        visibleCount: 0,
        selectedRegister: null,
        search: '',
        showCreateRegister: false,
        showExcelUpload: false,
        // A file handed over from outside (the assistant's ui.registers.open_import), loaded on open.
        importFile: null,
        showSchemaUpdate: false,
        showRegisterDataViewer: false,
        registerWorkspaceInitialTab: 'data',
        showExportDialog: false,
        registerToExport: null,
        auditRegister: null, // For audit sidebar context
        ExportMode,
        // API Data Source state
        showApiDataSources: false,
        showApiWizard: false,
        showApiHistory: false,
        editingApiDataSource: null,
        historyDataSource: null,
        registerHeaders: [
          { text: 'Name', value: 'name', sortable: true, class: 'rm-col-name', cellClass: 'rm-col-name' },
          { text: 'Owner', value: 'ownerName', sortable: true },
          { text: 'Type', value: 'type', sortable: true },
          { text: 'Status', value: 'status', sortable: true },
          { text: 'Assigned', value: 'assignedUsers', sortable: false },
          { text: 'Created', value: 'createDate', sortable: true },
          { text: 'Updated', value: 'updateDate', sortable: true },
          { text: 'Actions', value: 'actions', sortable: false, align: 'end', width: '156px' },
        ],
        facilities: [],
        groups: [],
      };
    },
    computed: {
      ...mapGetters('REGISTERS', {
        registers: 'GET_REGISTERS',
        loading: 'GET_LOADING',
        users: 'GET_USERS',
      }),

      ...mapGetters('ACCOUNT', {
        companyId: 'GET_COMPANY_ID',
      }),

      // Audit sidebar computed properties
      auditRegisterId() {
        return this.auditRegister?.id || null;
      },
      auditRegisterName() {
        return this.auditRegister?.name || '';
      },

      isAllOwners() {
        return this.ownerType === OWNER_TYPE.ALL;
      },
      showModeFilter() {
        return this.ownerType !== OWNER_TYPE.FACILITY;
      },
      ownerItems() {
        if (this.ownerType === OWNER_TYPE.FACILITY) return this.facilities;
        if (this.ownerType === OWNER_TYPE.GROUP) return this.groups;
        return [];
      },
      ownerPlaceholder() {
        return ownerSelectPlaceholder(this.ownerType);
      },
      ownerSelectIcon() {
        if (this.ownerType === OWNER_TYPE.FACILITY) return 'mdi-office-building-outline';
        if (this.ownerType === OWNER_TYPE.GROUP) return 'mdi-sitemap-outline';
        return '';
      },
      countLabel() {
        return registerCountLabel(this.visibleCount);
      },
      // Destructive menu items: error colour, lightened on dark surfaces for contrast.
      dangerTextClass() {
        return this.$vuetify.theme.dark ? 'red--text text--lighten-3' : 'error--text';
      },
      /**
       * Grid rows: owner and mode filters run before the copy. `ownerName` makes the Owner
       * column sortable and searchable; row actions still receive the source register.
       */
      tableRows() {
        const rows = [];
        for (const register of this.registers || []) {
          if (!matchesOwnerFilter(register, this.ownerType, this.selectedOwnerId)) continue;
          if (this.showModeFilter && !matchesModeFilter(register, this.modeFilter)) continue;
          const ownerView = registerOwnerPresentation(register);
          rows.push({ ...register, ownerName: ownerView.name, ownerView });
        }
        return rows;
      },
      /** Same actions and permission checks as before; only their placement changed. */
      rowActions() {
        return resolveRegisterRowActions((permissionKey) => this.$has(this.permissions.Registers[permissionKey]));
      },
    },
    methods: {
      $has,
      $formatDate,

      ...mapActions('REGISTERS', {
        fetchRegisters: 'FETCH_REGISTERS',
        fetchUsers: 'FETCH_USERS',
        deleteRegisterAction: 'DELETE_REGISTER',
        exportRegisterAction: 'EXPORT_REGISTER',
        openAuditSidebarRegister: 'OPEN_AUDIT_SIDEBAR_REGISTER',
      }),

      async fetchFacilities() {
        try {
          const res = await facilityService.get({ itemsPerPage: -1 });
          // API returns Page<FacilityVM> with { items: [...], total: n }
          // Axios wraps it in res.data, so access the inner items array
          this.facilities = res.data?.items || [];
        } catch (err) {
          error('RegisterManagement', 'Failed to fetch facilities:', err);
          this.facilities = [];
        }
      },

      async fetchGroups() {
        return this.$store.dispatch('GROUPS/GET_GROUPS').then((data) => {
          this.groups = data || [];
        });
      },

      getUserById(userId) {
        if (!userId || !this.users) return null;
        return this.users.find((user) => user.id === userId);
      },

      async initialize() {
        try {
          await Promise.all([
            this.fetchRegisters(),
            this.fetchFacilities(),
            this.fetchUsers(),
            this.fetchGroups(),
          ]);
        } catch (err) {
          error('RegisterManagement', 'Failed to initialize:', err);
          this.$dialog.notify.error('Failed to load register data');
        }
      },

      onOwnerChange(ownerId) {
        this.selectedOwnerId = ownerId || null;
        this.refreshRegisters();
      },

      /** A selected Facility is filtered server-side; everything else is filtered in tableRows. */
      async refreshRegisters() {
        try {
          await this.fetchRegisters(registerFetchParams(this.ownerType, this.selectedOwnerId));
        } catch (err) {
          error('RegisterManagement', 'Failed to load registers:', err);
          this.$dialog.notify.error('Failed to load registers');
        }
      },

      onPagination(pagination) {
        this.visibleCount = pagination?.itemsLength || 0;
      },

      /** Runs a row action's existing handler with the source register (not the decorated row). */
      runRowAction(action, row) {
        const register = (this.registers || []).find((r) => r.id === row.id) || row;
        this[action.method](register);
      },

      typeDotClass(type) {
        return registerTypeColorClass(this.getTypeText(type));
      },

      statusDotClass(status) {
        return registerStatusDotClass(this.getStatusText(status)) || 'rm-dot--hollow';
      },

      openCreateDialog() {
        this.selectedRegister = null;
        this.showCreateRegister = true;
      },

      editRegister(register) {
        this.selectedRegister = { ...register };
        this.showCreateRegister = true;
      },

      manageUsers(register) {
        this.selectedRegister = { ...register };
      },

      uploadExcel(register, file = null) {
        this.importFile = file;
        this.selectedRegister = { ...register };
        this.showExcelUpload = true;
      },

      updateSchema(register) {
        this.selectedRegister = { ...register };
        this.showSchemaUpdate = true;
      },

      viewRegisterData(register) {
        this.selectedRegister = { ...register };
        this.registerWorkspaceInitialTab = 'data';
        this.showRegisterDataViewer = true;
      },

      openRegisterRelationships(register) {
        this.selectedRegister = { ...register };
        this.registerWorkspaceInitialTab = 'relationships';
        this.showRegisterDataViewer = true;
      },

      closeRegisterDataViewer() {
        this.showRegisterDataViewer = false;
        this.selectedRegister = null;
      },

      async handleShowIn3d(tags) {
        const linkedTags = Array.isArray(tags) ? tags.filter(Boolean) : [tags].filter(Boolean);
        const registerId = this.selectedRegister?.id;
        const facilityId = this.selectedRegister?.facilityId || this.selectedRegister?.facility?.id;
        if (!linkedTags.length || !registerId || !facilityId) {
          this.$dialog.notify.warning('This register cannot be opened in the facility viewer');
          return;
        }

        this.$store.commit('TAGS_REGISTER/SET_ISOLATED_TAGS', linkedTags);
        this.showRegisterDataViewer = false;
        try {
          await this.$router.push({
            name: 'RegisterView',
            params: { id: facilityId, registerId },
            query: { showLinkedTagsIn3d: '1' },
          });
        } catch (err) {
          if (err?._isRouter === true || err?.name === 'NavigationDuplicated') return;
          error('RegisterManagement', 'Failed to open register in the facility viewer:', err);
          this.$dialog.notify.error('Failed to open the facility viewer');
        }
      },

      getRegisterTypeString(typeValue) {
        // Convert numeric register type to string format expected by RegisterTable
        if (!typeValue) return 'unknown';
        
        const typeMap = {
          [RegisterType.General]: 'general',
          [RegisterType.Equipment]: 'equipment', 
          [RegisterType.Pipeline]: 'pipeline',
          [RegisterType.Structural]: 'structural',
          [RegisterType.Inspection]: 'inspection',
          [RegisterType.Anomaly]: 'anomaly',
          [RegisterType.AvaInspect]: 'avaInspect',
          [RegisterType.Scaffold]: 'scaffold',
          [RegisterType.Permit]: 'permit',
          [RegisterType.Container]: 'container',
          [RegisterType.Operation]: 'operation',
        };
        
        return typeMap[typeValue] || 'unknown';
      },

      exportRegister(register) {
        this.registerToExport = { ...register };
        this.showExportDialog = true;
      },

      /**
       * Opens the audit history sidebar for a specific register
       * Shows register-level changes: creation, schema updates, user assignments, etc.
       * @param {Object} register - The register object
       */
      openRegisterAuditHistory(register) {
        if (!register || !register.id) {
          warn('Cannot open audit history: invalid register');
          return;
        }
        // Store the register context for the sidebar props
        this.auditRegister = { ...register };
        // Dispatch Vuex action to open sidebar in register mode
        this.openAuditSidebarRegister({
          registerId: register.id
        });
      },

      onExportComplete({ filename }) {
        this.$dialog.notify.success(`Export complete: ${filename}`);
        this.showExportDialog = false;
        this.registerToExport = null;
      },

      clearRegisterData(register) {
        this.$dialog.error({
          text: `This will permanently delete all data entries for "${register.name}". This action cannot be undone. Continue?`,
          title: 'Clear Register Data',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Clear All',
              color: 'warning',
              handle: async () => {
                try {
                  await this.$store.dispatch('REGISTERS/CLEAR_REGISTER_DATA', register.id);
                  this.$dialog.notify.success('All register data cleared');
                } catch (err) {
                  error('RegisterManagement', 'Clear data failed:', err);
                  this.$dialog.notify.error(clearRegisterDataErrorMessage(err));
                }
              },
            },
          },
        });
      },

      deleteRegister(register) {
        this.$dialog.error({
          text: `Are you sure you want to delete the register "${register.name}"? This action cannot be undone.`,
          title: 'Delete Register',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: async () => {
                try {
                  await this.deleteRegisterAction(register.id);
                  this.$dialog.notify.success('Register deleted successfully');
                } catch (err) {
                  error('RegisterManagement', 'Delete failed:', err);
                  this.$dialog.notify.error('Failed to delete register');
                }
              },
            },
          },
        });
      },

      onCreateRegisterSave(saved) {
        this.showCreateRegister = false;
        this.selectedRegister = null;
        // A new register usually lands on another page of the name-sorted list; filter to it so
        // the user sees what they just created (AVA-602 U14).
        if (saved && saved.created && saved.name) this.search = saved.name;
        this.refreshRegisters();
      },

      onExcelUploadComplete() {
        this.showExcelUpload = false;
        this.importFile = null;
        this.selectedRegister = null;
        this.refreshRegisters();
      },

      onSchemaUpdateComplete() {
        this.showSchemaUpdate = false;
        this.selectedRegister = null;
        this.refreshRegisters();
      },

      async handleNotificationDeepLink() {
        const { registerId, dataSourceId, view } = this.$route.query;
        if (!registerId) return;

        const register = this.registers.find((r) => String(r.id).toLowerCase() === String(registerId).toLowerCase());
        if (!register) {
          warn('RegisterManagement', 'Register not found for deep link:', registerId);
          if (view === REGISTER_IMPORT_ROUTE.VIEW) declineImportFile(registerId);
          return;
        }

        // ?registerId=…&view=import: the import dialog, with the file handed over (registerImport.js) loaded.
        if (view === REGISTER_IMPORT_ROUTE.VIEW) {
          const handoff = takeImportFile(registerId);
          this.uploadExcel(register, handoff ? handoff.file : null);
          if (handoff) handoff.opened(register.name);
          this.$router.replace({ query: {} }).catch(() => {});
          return;
        }

        this.selectedRegister = { ...register };
        this.showApiDataSources = true;

        if (view === 'history' && dataSourceId) {
          try {
            await this.$store.dispatch('REGISTERS/FETCH_API_DATA_SOURCES', { registerId });
            const dataSources = this.$store.getters['REGISTERS/GET_API_DATA_SOURCES'];
            const dataSource = dataSources.find((ds) => ds.id === dataSourceId);
            if (dataSource) {
              this.openApiHistory(dataSource);
            }
          } catch (err) {
            error('RegisterManagement', 'Failed to load data source for deep link:', err);
          }
        }

        // Clean query params after handling
        this.$router.replace({ query: {} }).catch(() => {});
      },

      // API Data Source methods
      openApiDataSources(register) {
        this.selectedRegister = { ...register };
        this.showApiDataSources = true;
      },

      openApiWizard() {
        this.editingApiDataSource = null;
        this.showApiWizard = true;
      },

      editApiDataSource(dataSource) {
        this.editingApiDataSource = dataSource;
        this.showApiDataSources = false;
        this.showApiWizard = true;
      },

      openApiHistory(dataSource) {
        this.historyDataSource = dataSource;
        this.showApiHistory = true;
      },

      /** History "Edit mapping": close the history and open the source in the wizard. */
      editApiDataSourceFromHistory(dataSource) {
        if (!dataSource) return;
        this.showApiHistory = false;
        this.editApiDataSource(dataSource);
      },

      onApiDataSourceComplete() {
        this.showApiWizard = false;
        this.editingApiDataSource = null;
        // Refresh the data source list if it's open
        if (this.showApiDataSources && this.selectedRegister) {
          this.$store.dispatch('REGISTERS/FETCH_API_DATA_SOURCES', {
            registerId: this.selectedRegister.id,
          });
        }
      },

      getTypeText(value) {
        if (typeof value === 'number') return getRegisterTypeText(value) || '—';
        if (!value) return '—';
        const s = String(value);
        return s.charAt(0).toUpperCase() + s.slice(1);
      },

      getStatusText(value) {
        if (typeof value === 'number') return getRegisterStatusText(value) || '—';
        return value || '—';
      },

      formatDate(dateStr) {
        if (!dateStr) return '—';
        try {
          const d = new Date(dateStr);
          return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
        } catch (e) {
          return dateStr;
        }
      },

      getUserInitials(name) {
        if (!name) return '?';
        return name
          .split(' ')
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      },
    },

    watch: {
      /** A new owner type clears the owner; a Facility view has no Mode filter. */
      ownerType(next) {
        const hadOwner = !!this.selectedOwnerId;
        this.selectedOwnerId = null;
        if (next === OWNER_TYPE.FACILITY) this.modeFilter = MODE_FILTER.ALL;
        // Only a selected Facility narrowed the server list; reload the full list after clearing it.
        if (hadOwner) this.refreshRegisters();
      },

      '$route.query': {
        handler(query) {
          if (query && query.registerId) {
            this.handleNotificationDeepLink();
          }
        },
      },
    },

    async created() {
      await this.initialize();
      this.handleNotificationDeepLink();
    },
  };
</script>

<style scoped>
  .register-management {
    height: 100%;
  }

  /* Flex layout for content + sidebar */
  .register-management-layout {
    display: flex;
    height: 100%;
    overflow: hidden;
  }

  .register-management-content {
    flex: 1;
    min-width: 0; /* Prevents flex item from overflowing */
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Make the main content sheet fill available space */
  .register-management-content > .v-sheet {
    flex: 1;
    height: auto !important; /* Override inline calc height */
    min-height: 0;
  }

  /* ---------------------------------------------------------------------------------------------
   * Management grid polish (showcase register-mode-visibility #surface=polish, Direction A).
   * Data-table standard: hairline rows, sticky header + Name column, "—" for empty, actions on
   * hover/focus. Rows are fixed at Comfortable (56px) because the Owner cell is two lines.
   * ------------------------------------------------------------------------------------------- */
  .rm-body {
    display: flex;
    flex-direction: column;
    gap: calc(var(--ds-space-sm) + var(--ds-space-xs));
    padding: var(--ds-space-md);
  }

  .rm-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: calc(var(--ds-space-sm) + var(--ds-space-xs));
  }

  /* Toolbar controls sit on the page background: give them the surface colour so they read as
     fields, and a visible hover on the owner-type toggle (Khalid review). */
  .rm-owner-type {
    background-color: var(--ds-surface-elevated) !important;
    border: thin solid var(--ds-border-default);
  }

  .rm-owner-type .v-btn:not(.v-btn--active):hover {
    background-color: var(--ds-control-hover) !important;
  }

  .rm-toolbar :deep(.v-input--is-disabled .v-input__slot),
  .rm-toolbar :deep(.v-text-field--outlined .v-input__slot) {
    background-color: var(--ds-surface-elevated) !important;
  }

  .rm-owner-type .v-btn {
    height: 40px !important; /* match the dense outlined fields */
    padding: 0 calc(var(--ds-space-sm) + var(--ds-space-xs)) !important;
    font-size: var(--ds-font-body-sm);
    letter-spacing: normal;
    text-transform: none;
  }

  .rm-owner-select {
    flex: 0 0 220px;
    max-width: 220px;
  }

  .rm-search {
    flex: 0 0 260px;
    max-width: 260px;
  }

  .rm-mode-filter {
    flex: 0 0 176px;
    max-width: 176px;
  }

  .rm-count {
    font-size: var(--ds-font-caption);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  @media (max-width: 599px) {
    .rm-owner-select,
    .rm-search,
    .rm-mode-filter {
      flex: 1 1 100%;
      max-width: none;
    }
  }

  .rm-table-shell {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: thin solid var(--table-border-hairline);
    border-radius: var(--ds-radius-md);
    overflow: hidden;
    isolation: isolate; /* keep sticky z-indices below the toolbar dropdowns */
  }

  .rm-table-shell > .rm-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--ds-surface-elevated);
  }

  .rm-table :deep(.v-data-table__wrapper) {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  /* Header: compact, muted, sticky (fixed-header) with a hairline under it */
  .rm-table :deep(thead tr:not(.v-data-table__progress) > th) {
    height: var(--table-row-compact) !important;
    font-size: var(--ds-font-caption) !important;
    font-weight: 600;
    white-space: nowrap;
    z-index: var(--z-table-sticky-header) !important;
    background-color: var(--ds-surface-elevated) !important;
    background-image: linear-gradient(var(--table-header-tint), var(--table-header-tint)) !important;
    color: var(--table-header-text) !important;
    border-bottom: 0 !important;
    box-shadow: inset 0 -1px 0 var(--table-border-hairline) !important;
  }

  /* Footer (pagination): compact and tinted like the header so the table reads as one framed block */
  /* Vuetify's fixed-header rule adds margin-right: 17px (scrollbar gutter) to the footer; the
     footer spans the full shell here, so drop it. Same specificity + scoped attribute wins. */
  .v-application--is-ltr .rm-table.v-data-table--fixed-header :deep(.v-data-footer) {
    margin-right: 0;
  }

  .rm-table :deep(.v-data-footer) {
    min-height: var(--table-row-compact);
    padding: 0 var(--ds-space-sm);
    border-top: thin solid var(--table-border-hairline) !important;
    background-color: var(--ds-surface-elevated);
    background-image: linear-gradient(var(--table-header-tint), var(--table-header-tint));
    font-size: var(--ds-font-caption);
  }

  .rm-table :deep(.v-data-footer__select),
  .rm-table :deep(.v-data-footer__pagination) {
    margin-top: 0;
    margin-bottom: 0;
  }

  .rm-table :deep(.v-data-footer__select .v-select) {
    margin: 0 0 0 var(--ds-space-md) !important;
    padding: 0 !important;
  }

  .rm-table :deep(.v-data-footer .v-btn) {
    width: 32px;
    height: 32px;
  }

  /* Rows: fixed Comfortable height, hairlines, token hover (no Vuetify grey fill) */
  .rm-table:not(.v-data-table--mobile) :deep(tbody > tr > td) {
    height: var(--table-row-comfortable) !important;
    white-space: nowrap;
    border-bottom: thin solid var(--table-border-hairline) !important;
  }

  .rm-table :deep(tbody > tr:last-child > td) {
    border-bottom: 0 !important;
  }

  .rm-table :deep(tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper)) {
    background: transparent !important;
  }

  .rm-table :deep(tbody > tr:not(.v-data-table__empty-wrapper):hover > td),
  .rm-table :deep(tbody > tr:not(.v-data-table__empty-wrapper):focus-within > td) {
    background-image: linear-gradient(var(--table-row-hover), var(--table-row-hover));
  }

  /* Sticky identity column (Name) */
  .rm-table:not(.v-data-table--mobile) :deep(.rm-col-name) {
    position: sticky;
    left: 0;
    z-index: var(--z-table-sticky-col);
    background-color: var(--ds-surface-elevated);
  }

  .rm-table:not(.v-data-table--mobile) :deep(thead th.rm-col-name) {
    z-index: var(--z-table-sticky-corner) !important;
  }

  .rm-name {
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 260px;
  }

  .rm-name__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  /* Owner block — two lines on purpose (Khalid), every other cell stays single-line */
  .rm-owner {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    max-width: 320px;
  }

  .rm-owner__icon {
    flex-shrink: 0;
  }

  .rm-owner__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    line-height: 1.25;
  }

  .rm-owner__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--ds-font-body);
  }

  .rm-owner__meta {
    display: flex;
    align-items: center;
    gap: var(--ds-space-xs);
    min-width: 0;
    font-size: var(--ds-font-caption);
    white-space: nowrap;
  }

  .rm-owner__mode {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .rm-owner__mode .v-icon {
    color: inherit;
  }

  .rm-owner__mode--set .v-icon {
    color: var(--v-primary-base);
  }

  .theme--dark .rm-owner__mode--set .v-icon {
    color: var(--v-secondary-base);
  }

  /* Reserved for the "has rows, no mode" warning once the list API exposes a row count */
  .rm-owner__mode--unset {
    color: var(--ds-color-pending);
    font-weight: 500;
  }

  /* Type / Status: coloured dot + text */
  .rm-dot-text {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-space-sm);
    white-space: nowrap;
  }

  .rm-dot {
    width: 8px;
    height: 8px;
    flex-shrink: 0;
    border-radius: var(--ds-radius-full);
  }

  .rm-dot--hollow {
    background-color: transparent !important;
    border: 1.5px solid currentColor;
    opacity: 0.6;
  }

  /* Created / Updated: the previous boxed date, kept by request */
  .date-cell {
    display: flex;
    align-items: center;
    width: fit-content;
    padding: 4px 8px;
    border: 1px solid #a8a8a875;
    border-radius: 4px;
    color: #6b778c;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* Row actions: 3 primary icons revealed on hover / keyboard focus, ⋮ always visible */
  .rm-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
  }

  .rm-actions__primary {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    opacity: 0;
    transition: opacity var(--ds-transition-fast);
  }

  .rm-table :deep(tbody > tr:hover .rm-actions__primary),
  .rm-table :deep(tbody > tr:focus-within .rm-actions__primary) {
    opacity: 1;
  }

  /* Touch: no hover, so the primary icons stay visible (nothing becomes unreachable) */
  @media (hover: none) {
    .rm-actions__primary {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .rm-actions__primary {
      transition: none;
    }
  }

  .rm-actions .v-btn:hover .v-icon,
  .rm-actions .v-btn:focus-visible .v-icon {
    color: var(--v-primary-base);
  }

  .theme--dark .rm-actions .v-btn:hover .v-icon,
  .theme--dark .rm-actions .v-btn:focus-visible .v-icon {
    color: var(--v-secondary-base);
  }

  .rm-menu {
    min-width: 224px;
  }

  .rm-menu__icon {
    margin-right: calc(var(--ds-space-sm) + var(--ds-space-xs)) !important;
  }

  :deep(.register-table) {
    margin: 0 !important;
  }
</style>
